import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";
import {
  Shield, ShieldCheck, ShieldOff, Key, LogOut, RefreshCw,
  AlertTriangle, CheckCircle2, Clock, Monitor, Smartphone,
  Activity, Eye, EyeOff, AlertCircle, XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const STRENGTH_COLORS = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-lime-500", "bg-emerald-500"];
const STRENGTH_LABELS = ["Muy débil", "Débil", "Aceptable", "Fuerte", "Muy fuerte"];

const SeguridadCuentaPage = () => {
  const { user, API, token, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [summary, setSummary] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/security/summary`, { headers });
      setSummary(res.data);
    } catch (_) { /* ignore */ }
  }, [API, token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-8" data-testid="seguridad-cuenta-page">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Shield className="w-6 h-6 text-blue-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Seguridad de mi Cuenta</h1>
          <p className="text-sm text-slate-500">Gestione su contraseña, MFA, sesiones y actividad reciente</p>
        </div>
      </div>

      {summary?.password?.force_change && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3" data-testid="alert-force-change">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">Debe cambiar su contraseña</h4>
            <p className="text-sm text-red-700">
              El período de gracia expiró. Cambie su contraseña ahora para continuar usando el sistema.
            </p>
          </div>
        </div>
      )}

      {summary?.password?.grace_days_remaining > 0 && !summary?.password?.compliant && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="alert-grace">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800">
              Su contraseña no cumple la nueva política
            </h4>
            <p className="text-sm text-amber-700">
              Tiene <strong>{summary.password.grace_days_remaining} días</strong> para actualizarla antes de que sea obligatorio.
            </p>
          </div>
        </div>
      )}

      {summary?.password?.expired && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">Su contraseña expiró</h4>
            <p className="text-sm text-red-700">
              Las contraseñas de funcionarios deben renovarse cada 90 días.
            </p>
          </div>
        </div>
      )}

      <ChangePasswordCard onChanged={fetchSummary} />
      <MFACard summary={summary} onChange={fetchSummary} />
      <SessionsCard onAfter={fetchSummary} />
      <ActivityCard />
    </div>
  );
};

// ==================== CAMBIO DE CONTRASEÑA ====================
const ChangePasswordCard = ({ onChanged }) => {
  const { API, token, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);
  const debounceRef = useRef(null);

  // Live validation against server (debounced) + client zxcvbn
  useEffect(() => {
    if (!next) { setPolicy(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/auth/password/validate`, { password: next }, { headers });
        setPolicy(res.data);
      } catch (_) {
        const client = zxcvbn(next);
        setPolicy({
          ok: false,
          score: client.score,
          strength_label: STRENGTH_LABELS[client.score],
          errors: [],
          suggestions: client.feedback.suggestions || [],
        });
      }
    }, 300);
  }, [next]);

  const clientScore = next ? zxcvbn(next).score : 0;
  const score = policy?.score ?? clientScore;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (!policy?.ok) {
      toast.error("La contraseña no cumple la política de seguridad");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/password/change`,
        { current_password: current, new_password: next },
        { headers }
      );
      toast.success(res.data.message);
      // Actualizar tokens en localStorage con los nuevos emitidos
      sessionStorage.setItem("access_token", res.data.access_token);
      sessionStorage.setItem("refresh_token", res.data.refresh_token);
      setCurrent(""); setNext(""); setConfirm("");
      onChanged && onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) {
        toast.error("Sesión cerrada por seguridad. Inicie sesión nuevamente.");
        setTimeout(() => { logout(); navigate("/login"); }, 2000);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.errors) {
        toast.error(detail.errors[0] || detail.message);
      } else {
        toast.error("Error al cambiar contraseña");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="w-5 h-5 text-blue-600" /> Cambiar Contraseña
        </CardTitle>
        <CardDescription>
          Las contraseñas deben cumplir la política de seguridad del sistema.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <Label>Contraseña actual</Label>
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="input-current-password"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Nueva contraseña</Label>
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {show ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            <Input
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              data-testid="input-new-password"
            />

            {next && (
              <div className="mt-2 space-y-2" data-testid="strength-feedback">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden flex">
                    {[0,1,2,3,4].map((i) => (
                      <div
                        key={i}
                        className={`flex-1 mx-[1px] transition-all ${i <= score ? STRENGTH_COLORS[score] : "bg-slate-200"}`}
                      />
                    ))}
                  </div>
                  <span className={`text-xs font-semibold ${score >= 3 ? "text-emerald-600" : score >= 2 ? "text-amber-600" : "text-red-600"}`}>
                    {policy?.strength_label || STRENGTH_LABELS[score]}
                  </span>
                </div>

                {policy && (
                  <div className="text-xs space-y-1">
                    {policy.errors?.length > 0 ? (
                      policy.errors.map((err, i) => (
                        <div key={i} className="flex items-start gap-1 text-red-600">
                          <XCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span>{err}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Cumple con la política de seguridad</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Confirmar nueva contraseña</Label>
            <Input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="input-confirm-password"
            />
            {confirm && next !== confirm && (
              <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={loading || !policy?.ok || next !== confirm}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-change-password"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Guardar cambios
            </Button>
          </div>

          {policy?.policy && (
            <div className="text-xs text-slate-500 border-t pt-3 mt-4">
              <strong className="text-slate-600">Política para su rol:</strong>{" "}
              ≥{policy.policy.min_length} caracteres,
              score zxcvbn ≥{policy.policy.min_zxcvbn_score}/4,
              {policy.policy.require_upper && " mayúscula,"}
              {policy.policy.require_lower && " minúscula,"}
              {policy.policy.require_digit && " número,"}
              {policy.policy.require_symbol && " símbolo."}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

// ==================== MFA ====================
const MFACard = ({ summary, onChange }) => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const isEnabled = !!summary?.mfa_enabled;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers });
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/verify`, { code }, { headers });
      toast.success("MFA habilitado");
      setSetupData(null); setCode("");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    const c = window.prompt("Ingrese su código MFA actual para deshabilitar:");
    if (!c) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: c }, { headers });
      toast.success("MFA deshabilitado");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="mfa-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isEnabled ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldOff className="w-5 h-5 text-amber-600" />}
          Autenticación en dos pasos (MFA)
          {isEnabled && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">Activo</Badge>}
          {!isEnabled && summary?.mfa_required_for_role && <Badge className="bg-amber-100 text-amber-700 border-amber-200 border">Recomendado</Badge>}
        </CardTitle>
        <CardDescription>
          Protege tu cuenta con códigos TOTP de Google Authenticator / Authy.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!setupData && (
          isEnabled ? (
            <Button variant="outline" onClick={handleDisable} disabled={loading} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-mfa-disable">
              Deshabilitar MFA
            </Button>
          ) : (
            <Button onClick={handleSetup} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-mfa-setup">
              <ShieldCheck className="w-4 h-4 mr-2" /> Habilitar MFA
            </Button>
          )
        )}
        {setupData && (
          <div className="mt-2 space-y-3 border-t pt-4" data-testid="mfa-setup-panel">
            <p className="text-sm font-medium">1. Escanee con su app autenticadora:</p>
            <img src={setupData.qr_code} alt="QR" className="w-48 h-48 border rounded bg-white p-2" />
            <p className="text-xs text-slate-500">O secreto manual: <code className="font-mono bg-slate-100 px-2 py-0.5 rounded select-all">{setupData.secret}</code></p>
            <p className="text-sm font-medium">2. Ingrese el código de 6 dígitos:</p>
            <div className="flex gap-2 max-w-xs">
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} className="font-mono text-center" data-testid="input-mfa-code" />
              <Button onClick={handleVerify} disabled={loading || code.length !== 6} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-mfa-verify">Confirmar</Button>
              <Button variant="ghost" onClick={() => { setSetupData(null); setCode(""); }}>Cancelar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== SESIONES ACTIVAS ====================
const SessionsCard = ({ onAfter }) => {
  const { API, token, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/auth/security/sessions`, { headers });
      setSessions(res.data || []);
    } catch (_) {}
  };
  useEffect(() => { fetch(); }, []);

  const handleRevoke = async (jti) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/security/sessions/${jti}`, { headers });
      toast.success("Sesión cerrada");
      fetch();
      onAfter && onAfter();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm("¿Cerrar TODAS las sesiones (incluyendo esta)? Deberá volver a iniciar sesión.")) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/security/sessions/revoke-all`, {}, { headers });
      toast.success("Todas las sesiones cerradas");
      setTimeout(() => { logout(); navigate("/login"); }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const getIcon = (ua) => {
    const u = (ua || "").toLowerCase();
    if (u.includes("mobile") || u.includes("android") || u.includes("iphone")) return <Smartphone className="w-4 h-4 text-slate-500" />;
    return <Monitor className="w-4 h-4 text-slate-500" />;
  };

  const getDevice = (ua) => {
    if (!ua) return "Desconocido";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edg")) return "Edge";
    return ua.substring(0, 40) + "...";
  };

  return (
    <Card data-testid="sessions-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5 text-indigo-600" /> Sesiones activas
              <Badge variant="outline">{sessions.length}</Badge>
            </CardTitle>
            <CardDescription>Dispositivos donde hay una sesión abierta</CardDescription>
          </div>
          {sessions.length > 0 && (
            <Button variant="outline" onClick={handleRevokeAll} disabled={loading} className="border-red-300 text-red-700" data-testid="btn-revoke-all">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar todas
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <p className="text-sm text-slate-500">No hay sesiones activas registradas.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`session-row-${idx}`}>
                <div className="flex items-center gap-3">
                  {getIcon(s.user_agent)}
                  <div>
                    <p className="text-sm font-medium">{getDevice(s.user_agent)}</p>
                    <p className="text-xs text-slate-500">
                      IP: <code>{s.ip || "-"}</code> · Inicio: {new Date(s.created_at).toLocaleString("es-VE")}
                    </p>
                  </div>
                </div>
                {s.refresh_jti && (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRevoke(s.refresh_jti)} disabled={loading} data-testid={`btn-revoke-${idx}`}>
                    <LogOut className="w-3 h-3 mr-1" /> Cerrar
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== ACTIVIDAD RECIENTE ====================
const ActivityCard = () => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/security/activity?limit=30`, { headers });
        setItems(res.data || []);
      } catch (_) {}
    })();
  }, []);

  const actionLabel = (a) => ({
    login_success: "Inicio de sesión",
    login_failed: "Intento fallido de login",
    logout: "Cierre de sesión",
    password_changed: "Contraseña cambiada",
    password_change_failed: "Intento fallido cambio de contraseña",
    password_change_forced_logout: "Cierre forzado por seguridad",
    mfa_enabled: "MFA activado",
    mfa_disabled: "MFA desactivado",
    mfa_challenge_failed: "MFA falló",
    session_revoked: "Sesión cerrada",
    session_revoked_all: "Cierre de todas las sesiones",
  })[a] || a;

  const actionIcon = (a) => {
    if (a.includes("failed") || a.includes("forced")) return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (a.includes("mfa_enabled") || a.includes("password_changed")) return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (a === "logout" || a.includes("session_revoked")) return <LogOut className="w-4 h-4 text-slate-500" />;
    return <Activity className="w-4 h-4 text-blue-500" />;
  };

  return (
    <Card data-testid="activity-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-violet-600" /> Actividad reciente
        </CardTitle>
        <CardDescription>Últimos 30 eventos de seguridad en su cuenta</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Sin actividad registrada.</p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {items.map((e, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 border-b text-sm last:border-b-0" data-testid={`activity-row-${idx}`}>
                {actionIcon(e.action)}
                <div className="flex-1">
                  <p className="font-medium text-slate-700">{actionLabel(e.action)}</p>
                  <p className="text-xs text-slate-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(e.timestamp).toLocaleString("es-VE")} · IP {e.ip || "-"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SeguridadCuentaPage;
